import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'tw tw:inline-flex tw:items-center tw:justify-center tw:gap-2 tw:whitespace-nowrap tw:rounded-xl tw:text-sm tw:font-medium tw:transition-all tw:disabled:pointer-events-none tw:disabled:opacity-50 tw:[&_svg]:pointer-events-none tw:[&_svg:not([class*=size-])]:size-4 tw:shrink-0 tw:[&_svg]:shrink-0 tw:outline-none tw:focus-visible:border-ring tw:focus-visible:ring-ring/50 tw:focus-visible:ring-[3px] tw:aria-invalid:ring-destructive/20 tw:dark:aria-invalid:ring-destructive/40 tw:aria-invalid:border-destructive',
  {
    variants: {
      variant: {
        default:
          'tw:bg-primary tw:text-primary-foreground tw:hover:bg-primary/90 tw:border-none tw:hover:text-white',
        destructive:
          'tw:bg-destructive tw:text-white tw:hover:bg-destructive/90 tw:focus-visible:ring-destructive/20 tw:dark:focus-visible:ring-destructive/40 tw:dark:bg-destructive/60',
        outline:
          'tw:border tw:bg-background tw:shadow-xs tw:hover:bg-accent tw:hover:text-accent-foreground tw:dark:bg-input/30 tw:dark:border-input tw:dark:hover:bg-input/50',
        secondary:
          'tw:bg-secondary tw:text-secondary-foreground tw:hover:bg-secondary/80',
        // The legacy nui `reverse` button (.nui-reverse): a white secondary that
        // reads stronger than outline's Cancel. No dark: classes: the app has no
        // dark theme, but Tailwind's dark variant follows prefers-color-scheme,
        // which is what turns outline grey on a dark-mode OS.
        reverse:
          'tw:border tw:border-[#c3c8ca] tw:bg-white tw:text-[var(--link)] tw:hover:border-[#727e82]',
        ghost:
          'tw:hover:bg-accent tw:hover:text-accent-foreground tw:dark:hover:bg-accent/50',
        link: 'tw:text-primary tw:underline-offset-4 tw:hover:underline',
      },
      size: {
        default: 'tw:h-9 tw:px-4 tw:py-2 tw:has-[>svg]:px-3',
        xs: 'tw:h-6 tw:gap-1 tw:px-2 tw:text-xs tw:has-[>svg]:px-1.5 tw:[&_svg:not([class*=size-])]:size-3',
        sm: 'tw:h-8 tw:gap-1.5 tw:px-3 tw:has-[>svg]:px-2.5',
        lg: 'tw:h-10 tw:px-6 tw:has-[>svg]:px-4',
        icon: 'tw:size-9',
        'icon-xs': 'tw:size-6 tw:[&_svg:not([class*=size-])]:size-3',
        'icon-sm': 'tw:size-8',
        'icon-lg': 'tw:size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

// forwardRef (not a plain function component) so Radix `asChild` triggers
// (PopoverTrigger, DropdownMenuTrigger, ...) can attach their anchor ref on
// React 18.
const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean;
    }
>(
  (
    {
      className,
      variant = 'default',
      size = 'default',
      asChild = false,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot.Root : 'button';

    return (
      <Comp
        ref={ref}
        data-slot="button"
        data-variant={variant}
        data-size={size}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

// A ghost Button styled as a form's top-right text action, so the row of
// actions above a form reads as one set. rounded-md! is load-bearing: it beats
// the legacy pill rule (buttonVariants' rounded-xl, --radius-xl 100px), which
// tailwind-merge does not drop for an important-postfix class.
const formTopActionBase =
  'tw:h-auto tw:gap-1 tw:rounded-md! tw:px-2 tw:py-1 tw:text-sm tw:text-primary tw:hover:text-primary';

/** The template dropdown's trigger: semibold, it carries a menu arrow. */
const formTopActionClass = `${formTopActionBase} tw:font-semibold`;

/** Both Quick Start undo links: hyperlink weight and colour, so the row does
 *  not read as two menus (NUI-5055). The underline goes on the label span, not
 *  here: on the button it would run under the leading icon as well. */
const formTopLinkClass = `${formTopActionBase} tw:font-normal`;

export { Button, buttonVariants, formTopActionClass, formTopLinkClass };
